// 세리사무소 포털 — 수익 기회와 이번 달 성과 (기획서 6.5).
//
// 카드를 눌러도 새 화면으로 가지 않는다. 위쪽 '오늘의 업무'의 같은 실행 패널에 제안 내용을
// 띄우고 그리로 스크롤한다 — 업무든 제안이든 판단하는 자리는 한 곳이라는 것이 이 화면의 규칙이다.
// 그래서 이 컴포넌트는 자기 상태가 없고(탭만 있다), 선택은 부모가 준 queue 를 통해 일어난다.
import React from 'react';
import { ALERTS, MONTHLY, OPPORTUNITIES, companyOf, won } from './Home.zeirishi.data';
import { useZeirishi } from './Home.zeirishi.context';
import type { RevTab } from './Home.zeirishi.context';
import type { WorkQueue } from './Home.zeirishi.work';
import styles from './Home.zeirishi.main.module.css';

const TABS: { k: RevTab; label: string }[] = [
  { k: 'all', label: '전체' },
  { k: '공공', label: '공공 · 정책자금' },
  { k: '금융', label: '금융 · 자금' },
];

interface Props {
  queue: WorkQueue;
  /** 카드를 열면 실행 패널로 데려간다 — 화면을 떠나지 않는다는 약속을 지키는 부분 */
  onOpenInPanel: () => void;
}

export default function ZeirishiRevenue({ queue, onOpenInPanel }: Props) {
  /* 고른 갈래는 컨텍스트에 있다 — 보기를 바꾸면 이 섹션이 화면의 다른 자리로 옮겨 가는데,
     여기 두면 옮길 때마다 풀린다. */
  const { revTab: tab, setRevTab: setTab } = useZeirishi();
  const list = tab === 'all' ? OPPORTUNITIES : OPPORTUNITIES.filter(o => o.grp === tab);

  /* 카드 폭 — 건수에 따라 12칸 그리드를 나눠 쓴다 */
  const span = list.length <= 2 ? 6 : list.length === 3 ? 4 : 3;

  const open = (id: string) => {
    queue.select(id);
    onOpenInPanel();
  };

  return (
    <section className={styles.revsec}>
      <div className={styles.wsec3}>
        <span>수익 기회와 이번 달 성과</span>
        <span className={styles.ln} />
        {/* 탭에 건수와 금액이 같이 찍힌다 — 열기 전에 어디에 시간을 쓸지 판단된다 */}
        <div className={styles.revtabs}>
          {TABS.map(t => {
            const arr = t.k === 'all' ? OPPORTUNITIES : OPPORTUNITIES.filter(o => o.grp === t.k);
            const sum = arr.reduce((a, b) => a + (b.fee || 0), 0);
            return (
              <button
                key={t.k}
                type="button"
                className={tab === t.k ? styles.revtabOn : undefined}
                onClick={() => setTab(t.k)}
              >
                {t.label} {arr.length}
                <span className={styles.s2}>{won(Math.round(sum / 10000))}만원</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.wboard}>
        {list.map(o => {
          const c = companyOf(o.co);
          const sent = queue.doneIds.indexOf(o.id) >= 0;
          return (
            <div
              key={o.id}
              className={`${styles.wc} ${styles['wcol' + span]} ${o.id === queue.selectedId ? styles.wcOn : ''}`}
              onClick={() => open(o.id)}
            >
              <span className={styles.cat2}>{o.cat}</span>
              <h4>{o.title}</h4>
              <div className={styles.co3}>{c.nm}</div>
              <div className={styles.fee}>
                <span className={styles.feeL}>예상 수수료</span>
                <span className={styles.feeV}>{won(o.fee || 0)}원</span>
                {/* 보낸 제안은 표시가 남아야 같은 곳에 두 번 보내지 않는다 */}
                {sent ? <span className={styles.sent}>발송됨</span> : null}
              </div>
            </div>
          );
        })}

        <div className={`${styles.wc} ${styles.wcSum} ${styles.wcol4}`}>
          <div className={styles.sumHead}>9월 누적 플랫폼 수익</div>
          <div className={styles.sumBig}>{won(MONTHLY.total)}<small>원</small></div>
          <div className={styles.cmp}>{MONTHLY.note}</div>
          <div className={styles.bars}>
            {MONTHLY.bars.map(b => (
              <div key={b.label} className={styles.br}>
                <div className={styles.brL}>{b.label}<b>{won(b.amount)}</b></div>
                <div className={styles.brT}><i style={{ width: `${b.pct}%`, background: b.color }} /></div>
              </div>
            ))}
          </div>
          <div className={styles.splitn}>
            수익 배분 <b>세무법인 70%</b> / 플랫폼 30% · 정산 예정 <b>10월 10일</b>
          </div>
        </div>

        <div className={`${styles.wc} ${styles.wcSum} ${styles.wcol8}`}>
          <div className={styles.sumHead}>수임기업 관련 변동 · 오늘 {ALERTS.length}건</div>
          <div className={styles.chksBlock}>
            {ALERTS.map(a => (
              <div key={a.text} className={styles.chkRow}>
                <span className={`${styles.chkOk} ${a.warn ? styles.chkWarn : ''}`}>{a.mark}</span>
                <span className={styles.chkTx}>{a.text}</span>
                <span className={styles.chkRr}>{a.when}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
